import { omit, pick } from 'lodash-es';
import { useEffect, useId } from 'react';
import { useRhinoContext } from '../RhinoContext';

export type RhinoDevtoolsEmitMessage = {
  type: string;
  context: null | object;
};

export type RhinoDevtoolsEmitOptions = {
  include?: string[];
  exclude?: string[];
};

const EMPTY_OPTIONS: RhinoDevtoolsEmitOptions = {};

type ContextWithPaths = {
  paths?: unknown[];
  [key: string]: unknown;
};

const reducedContext = (
  context: null | object,
  options: RhinoDevtoolsEmitOptions
): object => {
  let reduced = context as ContextWithPaths;
  if (options?.include) reduced = pick(reduced, options.include);
  if (options?.exclude) reduced = omit(reduced, options.exclude);

  // Replace non-string path elements (e.g. React nodes) with a placeholder.
  if (reduced?.paths && Array.isArray(reduced.paths)) {
    reduced = {
      ...reduced,
      paths: reduced.paths.map((value) =>
        typeof value === 'string' ? value : '<element>'
      )
    };
  }

  return reduced ?? {};
};

export const useRhinoDevtoolsEmit = (
  message: RhinoDevtoolsEmitMessage,
  options: RhinoDevtoolsEmitOptions = EMPTY_OPTIONS
): void => {
  const id = useId();
  const { devtoolsEventClient } = useRhinoContext();
  const { type, context } = message;
  const { enabled } = devtoolsEventClient;

  useEffect(() => {
    // No devtool consumes the registry unless the client is enabled (dev only),
    // so skip the work entirely otherwise. This matters under `isolate: false`
    // test runs, where the shared client would otherwise accumulate reports
    // from every model provider mount across the whole suite.
    if (!enabled) return;

    devtoolsEventClient.reportContext({
      id,
      type,
      context: reducedContext(context, options)
    });
  }, [enabled, devtoolsEventClient, id, type, context, options]);

  useEffect(
    () => () => {
      if (enabled) devtoolsEventClient.clearContext(id);
    },
    [enabled, devtoolsEventClient, id]
  );
};
